import React, { Component } from 'react';
import axios from 'axios';
import { passCsrfToken } from '../../helpers';
import { getPackageBookings } from '../../api/bookingApi';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import moment from 'moment';
import { EmptyContent } from '../shared';
import { PaymentMethodModal } from '../payments';
import { BASE_URL } from '../../constants';
import swal from 'sweetalert';
import { formatMoneyLegacy } from '../../utils/currency';

const STATUS_LABEL = {
  pending: 'Pending',
  processing: 'Confirmed',
  confirmed: 'Confirmed',
  verified: 'Paid',
  completed: 'Completed',
  declined: 'Rejected',
  cancelled: 'Cancelled'
};

const BADGE_TYPE = {
  pending: 'warning',
  processing: 'info',
  confirmed: 'success',
  verified: 'success',
  completed: 'success',
  declined: 'danger',
  cancelled: 'secondary'
};

class PackageBookings extends Component {
  constructor(props) {
    super(props);
    this.state = {
      bookings: [],
      loading: true,
      fetchError: null,
      payingBooking: null,
      showPaymentModal: false,
      paymentModalData: null
    };
  }

  componentDidMount() {
    passCsrfToken(document, axios);
    this.fetch();
    // Use visibility API instead of focus event to avoid triggering on button clicks
    // This only refreshes when user switches back to the tab
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  componentWillUnmount() {
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  }

  handleVisibilityChange = () => {
    if (!document.hidden) this.fetch();
  };

  // Pay flow (Private tab): open modal from list data (no package_bookings API call). User picks Khalti → POST /api/payments/initiate_khalti_payment only.
  handlePay = (booking) => {
    if (this.state.payingBooking) return;
    const inquiry = booking.inquiry || {};
    const dueAmount = booking.amount != null ? Number(booking.amount) : (inquiry.total_amount != null ? Number(inquiry.total_amount) : 0);
    if (dueAmount <= 0) return;
    this.setState({
      payingBooking: null,
      showPaymentModal: true,
      paymentModalData: {
        productIdentity: booking.idx,
        amount: dueAmount,
        productName: 'PACKAGE',
        currencyCode: booking.currency_code || pkg?.currency_code || 'NPR',
        productUrl: `${BASE_URL || ''}/bookings`,
        customerName: inquiry.first_name || inquiry.head_traveller_name || '',
        customerEmail: inquiry.email_address || '',
        customerPhone: inquiry.phone || ''
      }
    });
  };

  handleClosePaymentModal = () => {
    this.setState({ showPaymentModal: false, paymentModalData: null });
  };

  handlePaymentInitiated = () => {
    this.setState({ showPaymentModal: false, paymentModalData: null });
    // Defer list refresh so Khalti popup opens first; refresh silently so a failed request doesn't show a network error
    setTimeout(() => this.fetchSilent(), 1500);
  };

  fetchSilent = () => {
    getPackageBookings()
      .then((response) => {
        this.setState({
          bookings: response.data?.package_bookings || [],
          loading: false,
          fetchError: null
        });
      })
      .catch((error) => {
        console.warn('Package Booking List refresh after payment failed (silent):', error?.response?.status, error?.message);
      });
  };

  fetch = (params) => {
    this.setState({ loading: true, fetchError: null });
    getPackageBookings(params)
      .then((response) => {
        this.setState({
          bookings: response.data?.package_bookings || [],
          loading: false,
          fetchError: null
        });
      })
      .catch((error) => {
        const errMsg = error.response?.status === 401 ? null : (Array.isArray(error.response?.data?.errors) ? error.response.data.errors.join(' ') : (error.response?.data?.errors || 'Could not load your private tour bookings.'));
        this.setState({
          bookings: [],
          loading: false,
          fetchError: errMsg
        });
        if (error.response?.status === 401) {
          swal('Sign in required', 'Please sign in to view your private tour bookings.', 'info');
        }
        console.log('Package Booking List fetch error', error);
      });
  };

  render() {
    const { bookings, loading, fetchError } = this.state;

    if (loading) {
      return <p className="text-muted">Loading…</p>;
    }

    if (fetchError) {
      return <div className="alert alert-danger">{fetchError}</div>;
    }

    const list = Array.isArray(bookings) ? bookings : [];
    if (list.length === 0) {
      return <EmptyContent>No private tour bookings yet.</EmptyContent>;
    }

    return (
      <>
        <div className="">
        {list.map((booking) => {
          if (!booking || !booking.idx) return null;
          const pkg = booking.package || {};
          const inquiry = booking.inquiry || {};
          const status = inquiry.status || booking.status || 'pending';
          const statusLabel = STATUS_LABEL[status] || status;
          const badgeType = BADGE_TYPE[status] || 'light';
          const travelers = (inquiry.number_of_adult || 0) + (inquiry.number_of_child || 0);
          const hasDates = booking.start_date && booking.end_date;
          const preferredDate = inquiry.preferred_date;
          const datesDisplay = hasDates
            ? `${moment(booking.start_date).format('D MMM YYYY')} - ${moment(booking.end_date).format('D MMM YYYY')}`
            : (preferredDate ? moment(preferredDate).format('D MMM YYYY') : null);
          const fromTo = inquiry.package_from_to_display || pkg.from_to_display || (pkg.origin && pkg.destination ? `${pkg.origin} → ${pkg.destination}` : (pkg.origin || pkg.destination || '—'));
          const dueAmount = booking.amount != null ? Number(booking.amount) : (inquiry.total_amount != null ? Number(inquiry.total_amount) : 0);
          const showPayButton = status === 'processing' && dueAmount > 0;
          const cc = booking.currency_code || pkg?.currency_code || 'NPR';

          return (
            <div key={booking.idx} className="booking-card">
              <div className="booking-card__header">
                <h3 className="booking-card__title">{pkg.name || 'Private tour'}</h3>
                <div className="booking-card__meta">
                  {datesDisplay && (
                    <div className="booking-card__meta-item">
                      <i className="fas fa-calendar-alt" />
                      <span>{datesDisplay}</span>
                    </div>
                  )}
                  {fromTo && fromTo !== '—' && (
                    <div className="booking-card__meta-item">
                      <i className="fas fa-route" />
                      <span>{fromTo}</span>
                    </div>
                  )}
                  {travelers > 0 && (
                    <div className="booking-card__meta-item">
                      <i className="fas fa-users" />
                      <span>{travelers} {travelers === 1 ? 'person' : 'people'}</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="booking-card__footer">
                <div className="booking-card__status">
                  <span className={`booking-card__badge booking-card__badge--${badgeType}`}>
                    {statusLabel}
                  </span>
                </div>
                <div className="booking-card__actions">
                  {showPayButton && (
                    <button
                      type="button"
                      className="booking-card__action-btn booking-card__action-btn--primary"
                      onClick={() => this.handlePay(booking)}
                      disabled={!!this.state.payingBooking}
                    >
                      <i className="fas fa-credit-card" />
                      {this.state.payingBooking === booking.idx ? 'Opening…' : `Pay (${formatMoneyLegacy(dueAmount, cc)})`}
                    </button>
                  )}
                  <Link
                    to={{ pathname: `/package_booking/${booking.idx}` }}
                    className={showPayButton ? 'booking-card__action-btn booking-card__action-btn--outline' : 'booking-card__action-btn booking-card__action-btn--primary'}
                  >
                    {showPayButton ? 'View' : (status === 'verified' ? 'View' : 'View details')}
                  </Link>
                </div>
              </div>
            </div>
          );
        })}
        </div>

        <PaymentMethodModal
          open={this.state.showPaymentModal}
          onClose={this.handleClosePaymentModal}
          paymentParams={this.state.paymentModalData}
          amount={this.state.paymentModalData?.amount}
          onPaymentInitiate={this.handlePaymentInitiated}
        />
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  currentUser: state.userStore?.currentUser || {}
});

export default connect(mapStateToProps)(PackageBookings);
